import { Box, Snackbar } from "@material-ui/core"
import { useEffect, useState } from "react"
import { isLoggedIn, getTokenRacerId } from "../../utils/isLoggedIn"
import { FilledStarIcon, OutlinedStarIcon } from "../shared/icons"
import { RemoveConfirmationDialog } from "./RemoveConfirmationDialog"
import { LogInOrSignUpIndependent } from "../modal/LogInOrSignUpIndependent"
import {
  useGetFavoritesQuery,
  useAddFavoriteMutation,
  useRemoveFavoriteMutation
} from "../../api/reigniteApi"

type FavoriteButtonProps = {
  eventId: number
  isMobile: boolean
}

const LOCAL_STORAGE_ANON_FAVORITES_KEY = 'favorite-events-anon'

const FavoriteButton = ({
  eventId,
  isMobile,
}: FavoriteButtonProps) => {
  const isUserLoggedIn = isLoggedIn()
  const racerId = getTokenRacerId()

  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false)
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
  const [snackbarOptions, setSnackbarOptions] = useState({
    visible: false,
    message: '',
  })

  const { data: favoriteEventIds = [], refetch: refetchFavorites } = useGetFavoritesQuery(undefined, {
    skip: !isUserLoggedIn
  })
  const [addFavorite] = useAddFavoriteMutation()
  const [removeFavorite] = useRemoveFavoriteMutation()

  const getAnonymousFavorites = (): number[] => {
    const anonFavorites = localStorage.getItem(LOCAL_STORAGE_ANON_FAVORITES_KEY)
    return anonFavorites ? JSON.parse(anonFavorites) : []
  }

  const isFavorited = () => {
    if (isUserLoggedIn) {
      return favoriteEventIds.includes(eventId)
    }
    return getAnonymousFavorites().includes(eventId)
  }

  useEffect(() => {
    const syncAnonymousFavorites = async () => {
      if (isUserLoggedIn && racerId) {
        const anonFavorites = getAnonymousFavorites()
        if (anonFavorites.length > 0) {
          try {
            await Promise.all(anonFavorites.map(eventId => 
              addFavorite(eventId).unwrap()
            ))
            localStorage.removeItem(LOCAL_STORAGE_ANON_FAVORITES_KEY)
            refetchFavorites()
          } catch (error) {
            setSnackbarOptions({
              visible: true,
              message: 'Failed to sync favorites. Please try again.',
            })
          }
        }
      }
    }

    syncAnonymousFavorites()
  }, [isUserLoggedIn, racerId, addFavorite, refetchFavorites])

  const handleSnackbarOptions = (action: 'added' | 'removed' | 'closed' | 'pending') => {
    switch (action) {
      case 'added':
        setSnackbarOptions({
          visible: true,
          message: 'Event added to favorites',
        })
        break
      case 'removed':
        setSnackbarOptions({
          visible: true,
          message: 'Event removed from favorites',
        })
        break
      case 'pending':
        setSnackbarOptions({
          visible: true,
          message: 'Favorite will be saved after login',
        })
        break
      case 'closed':
        setSnackbarOptions((prev) => ({ ...prev, visible: false }))
        break
    }
  }

  const handleAnonymousFavorite = () => {
    const currentFavorites = getAnonymousFavorites()
    const newFavorites = currentFavorites.includes(eventId)
      ? currentFavorites.filter(id => id !== eventId)
      : [...currentFavorites, eventId]

    localStorage.setItem(LOCAL_STORAGE_ANON_FAVORITES_KEY, JSON.stringify(newFavorites))
  }

  const handleFavoriteClick = async () => {
    if (!isUserLoggedIn) {
      const wasFavorited = isFavorited()
      handleAnonymousFavorite()
      handleSnackbarOptions(wasFavorited ? 'removed' : 'pending')
      setIsAuthDialogOpen(true)
      return
    }

    if (isFavorited()) {
      setIsConfirmationDialogOpen(true)
    } else {
      try {
        await addFavorite(eventId).unwrap()
        handleSnackbarOptions('added')
      } catch (error) {
        setSnackbarOptions({
          visible: true,
          message: 'Failed to add favorite. Please try again.',
        })
      }
    }
  }

  const onSubmitRemoveFavorite = async () => {
    try {
      await removeFavorite(eventId).unwrap()
      handleSnackbarOptions('removed')
    } catch (error) {
      setSnackbarOptions({
        visible: true,
        message: 'Failed to remove favorite. Please try again.',
      })
    }
    setIsConfirmationDialogOpen(false)
  }

  const handleAuthClose = (reason?: string) => {
    if (reason === 'dismiss') {
      handleAnonymousFavorite() // Reverting the favorite change if user dismisses auth dialog
    }
    setIsAuthDialogOpen(false)
  }

  return (
    <>
      <Box marginRight='8px' onClick={handleFavoriteClick} style={{ cursor: 'pointer' }}>
        {isFavorited() ? <FilledStarIcon /> : <OutlinedStarIcon />}
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={snackbarOptions.visible}
        autoHideDuration={2500}
        onClose={() => handleSnackbarOptions('closed')}
        message={snackbarOptions.message}
      />

      <RemoveConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onClickClose={() => setIsConfirmationDialogOpen(false)}
        onClickSubmit={onSubmitRemoveFavorite}
      />

      <LogInOrSignUpIndependent
        firstSlideType='add-to-favorites'
        isMobile={isMobile}
        open={isAuthDialogOpen}
        closeAction={handleAuthClose}
      />
    </>
  )
}

export { FavoriteButton }